// Aufgeklappte Immobilienmappe („Weitere Details"): zuerst das Risiko selbst,
// dann Verträge, Schäden, Vorgänge zu EINEM Gebäude.
//
// Unterschied zur allgemeinen Objektmappe (`MappeDetails`): Dort steht je
// Vertrag eine Tabellenzeile mit neun Spalten. Hier ist der Vertrag ein
// eigener Block — Überschrift ist die Versicherungsart, darunter „Allgemein"
// (eine Zeile: Nummer, Gesellschaft mit Logo, Laufzeit, Zahlweise) und
// „Details" (Versicherungssumme, versicherte Gefahren mit Bildzeichen,
// Selbstbeteiligungen). Bei einer Gebäudeversicherung ist genau das die Frage:
// nicht „wie viele Verträge", sondern „was ist gedeckt und mit welchem
// Selbstbehalt".
//
// Geladen wird erst beim Aufklappen und dann je Gebäude einmal.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { apiFetch } from "../../utils/apiFetch";
import { IMMO } from "./immoTokens";
import { charOf, eur, type MappeRisk } from "./mappeDaten";
import { gefahrenAus, type GefahrZeile, type VertragCharLike } from "./vertragsGefahren";
import { gefahrIcon, IconRahmen } from "../../data/gefahrenIcons";
import { GesellschaftLogo } from "../redesign/contractRiskCells";
import { resolvePeople } from "../vorgang/shared/people";
import { listAppUsers, type AppUser } from "../../services/userService";

/* ── Typen (schlank: nur was die Mappe zeigt) ───────────────────────────── */

interface ImmoVertrag {
  id: number;
  contractNbr?: string | null;
  branch?: string | null;
  beginPolicyTerm?: string | null;
  endPolicyTerm?: string | null;
  endOfContract?: string | null;
  premiumAnnualGross?: number | string | null;
  paymentFrequency?: number | null;
  gesellschaftId?: number | null;
  gesellschaft?: { id?: number; name?: string | null } | null;
  insurer?: { id?: number; lastname?: string | null; displayName?: string | null } | null;
  characteristics?: VertragCharLike[] | null;
}

interface ImmoSchaden {
  id: number;
  damageNbr?: string | null;
  externalDamageNbr?: string | null;
  damageType?: string | null;
  damageDate?: string | null;
  status?: string | null;
  title?: string | null;
}

interface ImmoVorgang {
  id: number;
  titel?: string | null;
  status?: string | null;
  statusManuell?: string | null;
  frist?: string | null;
  twv?: string | null;
  bearbeiter?: unknown[] | null;
  hauptverantwortlich?: string[] | null;
}

/**
 * Was der Risiko-Abschnitt aus dem Fragebogen zeigt: Bauliches, das in der
 * Übersicht keinen Platz hat. Je Zeile die Merkmalsschlüssel in der
 * Reihenfolge, in der sie gelesen werden (neuer Schlüssel vor Altbestand).
 */
const RISIKO_ANGABEN: { label: string; keys: string[] }[] = [
  { label: "Baujahr", keys: ["baujahr"] },
  { label: "Bauartklasse", keys: ["bauartklasse"] },
  { label: "Geschosse", keys: ["geschosse"] },
  { label: "Dachform", keys: ["dachTyp", "dachform"] },
  { label: "Keller", keys: ["keller"] },
  { label: "Stellung", keys: ["stellung"] },
];

/* ── Formatierung ───────────────────────────────────────────────────────── */

const num = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
};

const datum = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("de-DE");
};

/** Contract.paymentFrequency = Zahlungen pro Jahr (siehe ZAHLWEISE_TO_FREQUENCY). */
const ZAHLWEISE: Record<number, string> = {
  1: "jährlich",
  2: "halbjährlich",
  4: "vierteljährlich",
  12: "monatlich",
};
const zahlweise = (f?: number | null): string =>
  f == null ? "—" : ZAHLWEISE[f] ?? `${f}× pro Jahr`;

const versichererName = (c: ImmoVertrag): string =>
  c.gesellschaft?.name || c.insurer?.displayName || c.insurer?.lastname || "—";

/**
 * Versicherungsart = `Contract.branch` (Sparte). Fehlt sie, steht dort nicht
 * „—", sondern der neutrale Oberbegriff: Die Zeile IST die Überschrift des
 * Blocks, und ein Gedankenstrich als Überschrift liest sich wie ein Fehler.
 */
const versicherungsart = (c: ImmoVertrag): string =>
  (c.branch ?? "").trim() || "Versicherungsvertrag";

/* ── Styles ─────────────────────────────────────────────────────────────── */

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    paddingTop: "14px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: IMMO.bgSoft,
    ...shorthands.borderTop("1px", "solid", IMMO.border),
  },
  block: { display: "flex", flexDirection: "column", rowGap: "8px" },
  blockHead: { display: "flex", alignItems: "baseline", columnGap: "8px" },
  blockTitle: {
    fontFamily: IMMO.fontAccent,
    fontSize: "0.86rem",
    fontWeight: 700,
    color: IMMO.ink,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  blockCount: { fontSize: "0.76rem", color: IMMO.muted },

  /* ── Vertrag als eigener Block ── */
  vertrag: {
    backgroundColor: IMMO.white,
    ...shorthands.border("1px", "solid", IMMO.border),
    ...shorthands.borderRadius("10px"),
    ...shorthands.overflow("hidden"),
  },
  vertragKopf: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: IMMO.cardBg,
    ...shorthands.borderBottom("1px", "solid", IMMO.border),
  },
  vertragArt: {
    fontFamily: IMMO.fontAccent,
    fontSize: "1rem",
    fontWeight: 700,
    color: IMMO.ink,
  },
  vertragOeffnen: {
    marginLeft: "auto",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    color: IMMO.blau,
    fontFamily: IMMO.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "9px",
    paddingRight: "9px",
    ...shorthands.border("1px", "solid", IMMO.border),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: IMMO.bgSelect },
  },
  teil: {
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  teilTrenner: { ...shorthands.borderTop("1px", "dashed", IMMO.border) },
  teilLabel: {
    display: "block",
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: IMMO.muted,
    marginBottom: "6px",
  },
  /** „Allgemein" ist bewusst EINE Zeile — so verlangt es die Vorlage. */
  allgemeinZeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    rowGap: "6px",
    flexWrap: "wrap",
    fontSize: "0.84rem",
    color: IMMO.ink,
  },
  paarKey: { fontSize: "0.72rem", color: IMMO.muted, fontWeight: 600, marginRight: "3px" },
  nr: { fontWeight: 700, color: IMMO.blau, fontVariantNumeric: "tabular-nums" },
  punkt: { color: IMMO.border, userSelect: "none" },
  vsZeile: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "8px",
    marginBottom: "10px",
  },
  vsWert: {
    fontFamily: IMMO.fontAccent,
    fontSize: "1.15rem",
    fontWeight: 700,
    color: IMMO.ink,
    fontVariantNumeric: "tabular-nums",
  },

  /* ── Tabelle der versicherten Gefahren ── */
  tabelle: { borderCollapse: "collapse", width: "100%", fontSize: "0.8rem" },
  th: {
    fontSize: "0.64rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: IMMO.muted,
    backgroundColor: IMMO.bgSoft,
    textAlign: "left",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "9px",
    paddingRight: "9px",
    whiteSpace: "nowrap",
    ...shorthands.borderBottom("1px", "solid", IMMO.border),
  },
  thRight: { textAlign: "right" },
  td: {
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "9px",
    paddingRight: "9px",
    color: IMMO.ink,
    verticalAlign: "middle",
    ...shorthands.borderBottom("1px", "solid", IMMO.borderDashed),
  },
  tdRight: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  tdMuted: { color: IMMO.muted },
  gefahrZelle: { display: "flex", alignItems: "center", columnGap: "8px" },
  gefahrIco: { width: "18px", height: "18px", flexShrink: 0, color: IMMO.blau, display: "inline-flex" },

  /* ── Selbstbeteiligungen: eigene Zeile UNTER dem Gefahren-Kopf ── */
  sbZeile: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "6px",
    marginTop: "8px",
    alignItems: "center",
  },
  sbChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.75rem",
    color: IMMO.ink,
    backgroundColor: IMMO.bgSoft,
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "8px",
    paddingRight: "9px",
    ...shorthands.border("1px", "solid", IMMO.border),
    ...shorthands.borderRadius("999px"),
  },
  sbIco: { width: "13px", height: "13px", flexShrink: 0, color: IMMO.blau, display: "inline-flex" },
  sbWert: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },

  /* ── Schäden / Vorgänge ── */
  tabelleWrap: {
    overflowX: "auto",
    backgroundColor: IMMO.white,
    ...shorthands.border("1px", "solid", IMMO.border),
    ...shorthands.borderRadius("10px"),
  },
  linkRow: { cursor: "pointer", ":hover": { backgroundColor: IMMO.bgSoft } },
  linkNr: {
    color: IMMO.blau,
    fontWeight: 700,
    textDecorationLine: "underline",
    textDecorationColor: IMMO.border,
    textUnderlineOffset: "3px",
    whiteSpace: "nowrap",
  },
  klein: { fontSize: "0.72rem", color: IMMO.muted },
  leer: { fontSize: "0.78rem", color: IMMO.muted, fontStyle: "italic" },
});

/* ── Vertragsblock ──────────────────────────────────────────────────────── */

function VertragBlock({
  vertrag,
  objektVs,
  onOeffnen,
}: {
  vertrag: ImmoVertrag;
  /** Versicherungssumme des Gebäudes — Rückfall, wenn der Vertrag keine führt. */
  objektVs: number | null;
  onOeffnen: () => void;
}) {
  const styles = useStyles();
  const gefahren: GefahrZeile[] = gefahrenAus(vertrag.characteristics);
  // Die Versicherungssumme ist im Datenmodell am Risikoobjekt gepflegt, nicht
  // am Vertrag. Summieren die Gefahren sich zu einem Betrag, gilt dieser —
  // sonst die des Gebäudes.
  const vsGefahren = gefahren.reduce((acc, g) => acc + (g.vs ?? 0), 0);
  const vs = vsGefahren > 0 ? vsGefahren : objektVs;
  const praemie = num(vertrag.premiumAnnualGross);
  const mitSb = gefahren.filter((g) => g.sb);

  const punkt = <span className={styles.punkt} aria-hidden>·</span>;

  return (
    <div className={styles.vertrag}>
      <div className={styles.vertragKopf}>
        <span className={styles.vertragArt}>{versicherungsart(vertrag)}</span>
        <button type="button" className={styles.vertragOeffnen} onClick={onOeffnen}>
          Vertrag öffnen →
        </button>
      </div>

      <div className={styles.teil}>
        <span className={styles.teilLabel}>Allgemein</span>
        <div className={styles.allgemeinZeile}>
          <span>
            <span className={styles.paarKey}>Vertragsnr.</span>
            <span className={styles.nr}>{vertrag.contractNbr || `#${vertrag.id}`}</span>
          </span>
          {punkt}
          <GesellschaftLogo
            id={vertrag.gesellschaft?.id ?? vertrag.gesellschaftId ?? null}
            name={versichererName(vertrag)}
          />
          {punkt}
          <span>
            <span className={styles.paarKey}>Beginn</span>
            {datum(vertrag.beginPolicyTerm)}
          </span>
          {punkt}
          <span>
            <span className={styles.paarKey}>Ablauf</span>
            {datum(vertrag.endPolicyTerm ?? vertrag.endOfContract)}
          </span>
          {punkt}
          <span>
            <span className={styles.paarKey}>Zahlweise</span>
            {zahlweise(vertrag.paymentFrequency)}
          </span>
          {praemie != null && (
            <>
              {punkt}
              <span>
                <span className={styles.paarKey}>Jahresprämie</span>
                {eur(praemie)}
              </span>
            </>
          )}
        </div>
      </div>

      <div className={mergeClasses(styles.teil, styles.teilTrenner)}>
        <span className={styles.teilLabel}>Details</span>
        <div className={styles.vsZeile}>
          <span className={styles.paarKey}>Versicherungssumme</span>
          <span className={styles.vsWert}>{vs != null && vs > 0 ? eur(vs) : "nicht erfasst"}</span>
        </div>

        {gefahren.length === 0 ? (
          <div className={styles.leer}>
            Zu diesem Vertrag sind keine versicherten Gefahren erfasst.
          </div>
        ) : (
          <>
            <table className={styles.tabelle}>
              <thead>
                <tr>
                  <th className={styles.th}>Versicherte Gefahr</th>
                  <th className={mergeClasses(styles.th, styles.thRight)}>Versicherungssumme</th>
                </tr>
              </thead>
              <tbody>
                {gefahren.map((g) => (
                  <tr key={g.id}>
                    <td className={styles.td}>
                      <span className={styles.gefahrZelle}>
                        <span className={styles.gefahrIco}>
                          <IconRahmen titel={g.label}>{gefahrIcon(g.id)}</IconRahmen>
                        </span>
                        {g.label}
                      </span>
                    </td>
                    <td className={mergeClasses(styles.td, styles.tdRight)}>
                      {g.vs != null ? eur(g.vs) : <span className={styles.tdMuted}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Selbstbeteiligungen stehen UNTER der Gefahrentabelle, nicht als
                weitere Spalte darin: Sie sind nicht je Gefahr gepflegt (nur
                wenige tragen einen Selbstbehalt), und eine Spalte voller
                Gedankenstriche verdeckt die wenigen, auf die es ankommt. */}
            <span className={styles.teilLabel} style={{ marginTop: 12 }}>
              Selbstbeteiligungen
            </span>
            {mitSb.length === 0 ? (
              <div className={styles.leer}>Keine Selbstbeteiligung vereinbart.</div>
            ) : (
              <div className={styles.sbZeile}>
                {mitSb.map((g) => (
                  <span key={g.id} className={styles.sbChip} title={g.label}>
                    <span className={styles.sbIco}>
                      <IconRahmen>{gefahrIcon(g.id)}</IconRahmen>
                    </span>
                    {g.label}
                    <span className={styles.sbWert}>{g.sb}</span>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Komponente ─────────────────────────────────────────────────────────── */

export function ImmobilienMappeDetails({
  risk,
  versicherungssumme,
}: {
  risk: MappeRisk;
  /** Versicherungssumme des Gebäudes (Rückfall für die Vertragsdetails). */
  versicherungssumme?: number | null;
}) {
  const styles = useStyles();
  const navigate = useNavigate();
  const riskId = risk.id;
  const angaben = RISIKO_ANGABEN.map((a) => ({ label: a.label, wert: charOf(risk, ...a.keys) })).filter(
    (a): a is { label: string; wert: string } => !!a.wert,
  );

  // Bearbeiter/Hauptverantwortliche sind User-Ids — die Namensauflösung braucht
  // die User-Liste (modulweit gecacht, siehe listAppUsers).
  const [users, setUsers] = useState<AppUser[]>([]);
  useEffect(() => {
    let cancelled = false;
    listAppUsers()
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch(() => {
        /* Namen bleiben dann die Rohschlüssel — kein Grund, die Mappe zu leeren */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [vertraege, setVertraege] = useState<ImmoVertrag[]>([]);
  const [schaeden, setSchaeden] = useState<ImmoSchaden[]>([]);
  const [vorgaenge, setVorgaenge] = useState<ImmoVorgang[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiFetch<ImmoVertrag[]>(`contracts/by-risk/${riskId}`)
        .then(({ data }) => (Array.isArray(data) ? data : []))
        .catch(() => [] as ImmoVertrag[]),
      apiFetch<ImmoSchaden[]>(`damages/by-risk/${riskId}`)
        .then(({ data }) => (Array.isArray(data) ? data : []))
        .catch(() => [] as ImmoSchaden[]),
      apiFetch<ImmoVorgang[]>(`vorgaenge/by-risk/${riskId}`)
        .then(({ data }) => (Array.isArray(data) ? data : []))
        .catch(() => [] as ImmoVorgang[]),
    ])
      .then(([c, d, v]) => {
        if (cancelled) return;
        setVertraege(c);
        setSchaeden(d);
        setVorgaenge(v);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [riskId]);

  const personen = (v: ImmoVorgang, feld: "bearbeiter" | "hauptverantwortlich"): string => {
    const raw = feld === "bearbeiter" ? v.bearbeiter : v.hauptverantwortlich;
    const people = resolvePeople((raw as unknown[]) ?? [], users);
    return people.length ? people.map((p) => p.name).join(", ") : "—";
  };

  // Erster Abschnitt: das Risiko — es steht schon vor dem Laden, die Merkmale
  // kommen aus der Übersicht mit.
  const risikoBlock = (
    <div className={styles.block}>
      <div className={styles.blockHead}>
        <span className={styles.blockTitle}>Risiko</span>
      </div>
      <div className={styles.vertrag}>
        <div className={styles.vertragKopf}>
          <span className={styles.vertragArt}>
            {(risk.name ?? "").trim() || charOf(risk, "typ") || "Immobilie"}
          </span>
          <button
            type="button"
            className={styles.vertragOeffnen}
            onClick={() => navigate(`/risks/${riskId}`)}
          >
            Risiko öffnen →
          </button>
        </div>
        <div className={styles.teil}>
          {angaben.length === 0 ? (
            <div className={styles.leer}>Keine baulichen Angaben erfasst.</div>
          ) : (
            <div className={styles.allgemeinZeile}>
              {angaben.map((a, i) => (
                <span key={a.label} style={{ display: "inline-flex", alignItems: "center", columnGap: 8 }}>
                  {i > 0 && <span className={styles.punkt}>·</span>}
                  <span>
                    <span className={styles.paarKey}>{a.label}</span>
                    {a.wert}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.root}>
        {risikoBlock}
        <div className={styles.leer}>Verträge, Schäden und Vorgänge werden geladen …</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {risikoBlock}

      {/* Verträge */}
      <div className={styles.block}>
        <div className={styles.blockHead}>
          <span className={styles.blockTitle}>Verträge</span>
          <span className={styles.blockCount}>{vertraege.length}</span>
        </div>
        {vertraege.length === 0 ? (
          <div className={styles.leer}>Kein Vertrag zu diesem Gebäude.</div>
        ) : (
          vertraege.map((c) => (
            <VertragBlock
              key={c.id}
              vertrag={c}
              objektVs={versicherungssumme ?? null}
              onOeffnen={() => navigate(`/contracts/${c.id}`)}
            />
          ))
        )}
      </div>

      {/* Schäden */}
      <div className={styles.block}>
        <div className={styles.blockHead}>
          <span className={styles.blockTitle}>Schäden</span>
          <span className={styles.blockCount}>{schaeden.length}</span>
        </div>
        {schaeden.length === 0 ? (
          <div className={styles.leer}>Kein Schaden zu diesem Gebäude.</div>
        ) : (
          <div className={styles.tabelleWrap}>
            <table className={styles.tabelle}>
              <thead>
                <tr>
                  <th className={styles.th}>Schadennr. Courté</th>
                  <th className={styles.th}>Schadennr. VR</th>
                  <th className={styles.th}>Schadenart</th>
                  <th className={styles.th}>Schadentag</th>
                  <th className={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {schaeden.map((d) => (
                  <tr
                    key={d.id}
                    className={styles.linkRow}
                    onClick={() => navigate(`/schaeden/${d.id}`)}
                  >
                    <td className={styles.td}>
                      <span className={styles.linkNr}>{d.damageNbr || `#${d.id}`}</span>
                      {d.title && <div className={styles.klein}>{d.title}</div>}
                    </td>
                    <td className={mergeClasses(styles.td, styles.tdMuted)}>
                      {d.externalDamageNbr || "—"}
                    </td>
                    <td className={styles.td}>{d.damageType || "—"}</td>
                    <td className={styles.td}>{datum(d.damageDate)}</td>
                    <td className={styles.td}>{d.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Vorgänge — Zuordnung am Risiko ODER an einem seiner Verträge */}
      <div className={styles.block}>
        <div className={styles.blockHead}>
          <span className={styles.blockTitle}>Vorgänge</span>
          <span className={styles.blockCount}>{vorgaenge.length} · Risiko und Vertrag</span>
        </div>
        {vorgaenge.length === 0 ? (
          <div className={styles.leer}>Kein Vorgang zu diesem Gebäude oder seinen Verträgen.</div>
        ) : (
          <div className={styles.tabelleWrap}>
            <table className={styles.tabelle}>
              <thead>
                <tr>
                  <th className={styles.th}>Titel</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Hauptv.</th>
                  <th className={styles.th}>Bearbeiter</th>
                  <th className={styles.th}>Frist</th>
                  <th className={styles.th}>Wiedervorlage</th>
                </tr>
              </thead>
              <tbody>
                {vorgaenge.map((v) => (
                  <tr
                    key={v.id}
                    className={styles.linkRow}
                    onClick={() => navigate(`/vorgaenge/${v.id}`)}
                  >
                    <td className={styles.td}>
                      <span className={styles.linkNr}>{v.titel || `Vorgang #${v.id}`}</span>
                    </td>
                    <td className={styles.td}>{v.status || v.statusManuell || "—"}</td>
                    <td className={mergeClasses(styles.td, styles.tdMuted)}>
                      {personen(v, "hauptverantwortlich")}
                    </td>
                    <td className={styles.td}>{personen(v, "bearbeiter")}</td>
                    <td className={styles.td}>{datum(v.frist)}</td>
                    <td className={styles.td}>{datum(v.twv)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
